class ReiandoBestSeller {
  constructor(section) {
    this.section = section;
    this.viewport = section.querySelector('[data-best-seller-viewport]');
    this.track = section.querySelector('[data-best-seller-track]');
    this.items = Array.from(
      section.querySelectorAll('[data-best-seller-item]')
    );
    this.prevButton = section.querySelector('[data-best-seller-prev]');
    this.nextButton = section.querySelector('[data-best-seller-next]');
    this.dotsContainer = section.querySelector('[data-best-seller-dots]');
    this.status = section.querySelector('[data-best-seller-status]');

    if (
      !this.viewport ||
      !this.track ||
      this.items.length === 0 ||
      !this.prevButton ||
      !this.nextButton ||
      !this.dotsContainer
    ) {
      return;
    }

    this.currentPage = 0;
    this.startX = 0;
    this.currentX = 0;
    this.isDragging = false;
    this.wasMobile = this.isMobile();

    this.handleResize = this.handleResize.bind(this);
    this.handlePrevious = this.handlePrevious.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);

    this.prevButton.addEventListener('click', this.handlePrevious);
    this.nextButton.addEventListener('click', this.handleNext);
    this.viewport.addEventListener('keydown', this.handleKeydown);
    this.viewport.addEventListener('touchstart', this.handleTouchStart, {
      passive: true
    });
    this.viewport.addEventListener('touchmove', this.handleTouchMove, {
      passive: true
    });
    this.viewport.addEventListener('touchend', this.handleTouchEnd);

    window.addEventListener('resize', this.handleResize);

    this.update();
  }

  isMobile() {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  getItemsPerPage() {
    return 1;
  }

  getPageCount() {
    if (!this.isMobile()) {
      return 1;
    }

    return Math.max(
      1,
      Math.ceil(this.items.length / this.getItemsPerPage())
    );
  }

  handleResize() {
    const isMobile = this.isMobile();

    if (isMobile !== this.wasMobile) {
      this.currentPage = 0;
      this.wasMobile = isMobile;
    }

    const pageCount = this.getPageCount();

    if (this.currentPage > pageCount - 1) {
      this.currentPage = pageCount - 1;
    }

    this.update();
  }

  handlePrevious() {
    if (!this.isMobile() || this.currentPage <= 0) return;

    this.currentPage -= 1;
    this.update();
  }

  handleNext() {
    if (
      !this.isMobile() ||
      this.currentPage >= this.getPageCount() - 1
    ) {
      return;
    }

    this.currentPage += 1;
    this.update();
  }

  handleKeydown(event) {
    if (!this.isMobile()) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.handlePrevious();
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.handleNext();
    }
  }

  handleTouchStart(event) {
    if (!this.isMobile()) return;

    this.startX = event.touches[0].clientX;
    this.currentX = this.startX;
    this.isDragging = true;
  }

  handleTouchMove(event) {
    if (!this.isMobile() || !this.isDragging) return;

    this.currentX = event.touches[0].clientX;
  }

  handleTouchEnd() {
    if (!this.isMobile() || !this.isDragging) return;

    const distance = this.currentX - this.startX;
    const threshold = 50;

    if (distance > threshold) {
      this.handlePrevious();
    } else if (distance < -threshold) {
      this.handleNext();
    }

    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
  }

  createDots() {
    this.dotsContainer.innerHTML = '';

    if (!this.isMobile()) {
      return;
    }

    const pageCount = this.getPageCount();

    for (let index = 0; index < pageCount; index += 1) {
      const dot = document.createElement('button');

      dot.type = 'button';
      dot.className = 'reiando-best-seller__dot';
      dot.setAttribute(
        'aria-label',
        `${index + 1}ページ目の商品を表示`
      );

      if (index === this.currentPage) {
        dot.classList.add('is-active');
        dot.setAttribute('aria-current', 'true');
      }

      dot.addEventListener('click', () => {
        this.currentPage = index;
        this.update();
      });

      this.dotsContainer.appendChild(dot);
    }
  }

  updateTrack() {
    if (!this.isMobile()) {
      this.track.style.transform = 'none';
      this.viewport.setAttribute('tabindex', '-1');
      this.viewport.removeAttribute('aria-roledescription');

      this.items.forEach((item) => {
        item.setAttribute('aria-hidden', 'false');

        const links = item.querySelectorAll('a, button');

        links.forEach((link) => {
          link.tabIndex = 0;
        });
      });

      return;
    }

    this.viewport.setAttribute('tabindex', '0');
    this.viewport.setAttribute('aria-roledescription', 'カルーセル');

    const itemsPerPage = this.getItemsPerPage();
    const translatePercent = this.currentPage * 100;

    this.track.style.transform = `translateX(-${translatePercent}%)`;

    this.items.forEach((item, index) => {
      const firstVisibleIndex = this.currentPage * itemsPerPage;
      const lastVisibleIndex = firstVisibleIndex + itemsPerPage - 1;
      const isVisible =
        index >= firstVisibleIndex && index <= lastVisibleIndex;

      item.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

      const links = item.querySelectorAll('a, button');

      links.forEach((link) => {
        link.tabIndex = isVisible ? 0 : -1;
      });
    });
  }

  updateButtons() {
    if (!this.isMobile()) {
      this.prevButton.disabled = true;
      this.nextButton.disabled = true;
      return;
    }

    const pageCount = this.getPageCount();

    this.prevButton.disabled = this.currentPage === 0;
    this.nextButton.disabled = this.currentPage === pageCount - 1;
  }

  updateStatus() {
    if (!this.status) return;

    if (!this.isMobile()) {
      this.status.textContent = '';
      return;
    }

    this.status.textContent =
      `${this.currentPage + 1} / ${this.getPageCount()}ページ`;
  }

  update() {
    this.createDots();
    this.updateTrack();
    this.updateButtons();
    this.updateStatus();
  }
}

function initializeReiandoBestSeller(root = document) {
  const sections = [];

  if (
    root instanceof Element &&
    root.matches('[data-reiando-best-seller]')
  ) {
    sections.push(root);
  }

  root
    .querySelectorAll?.('[data-reiando-best-seller]')
    .forEach((section) => {
      sections.push(section);
    });

  sections.forEach((section) => {
    if (section.dataset.bestSellerInitialized === 'true') return;

    section.dataset.bestSellerInitialized = 'true';
    new ReiandoBestSeller(section);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initializeReiandoBestSeller();
});

document.addEventListener('shopify:section:load', (event) => {
  initializeReiandoBestSeller(event.target);
});
